import { useState } from 'react';
import { Dropdown, Tag, Spin, message, type MenuProps } from 'antd';
import {
  LogoutOutlined,
  ExperimentOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AssumeUserModal from '../debug/AssumeUserModal';

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentUser,
    logout,
    debugModeAvailable,
    isImpersonating,
    stopImpersonation,
  } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const [assumeModalOpen, setAssumeModalOpen] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
    }
  };

  const initials = currentUser?.full_name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'U';

  const roleLabel = currentUser?.user_type_name
    || (currentUser?.is_admin ? 'Administrator' : null);

  const handleStopImpersonating = async () => {
    try {
      await stopImpersonation();
      window.location.reload();
    } catch (err: unknown) {
      const errorMessage = (
        typeof err === 'object'
        && err !== null
        && 'response' in err
        && typeof (err as { response?: unknown }).response === 'object'
        && (err as { response?: { data?: { error?: string } } }).response?.data?.error
      ) ? (err as { response?: { data?: { error?: string } } }).response?.data?.error : null;
      message.error(errorMessage || 'Could not stop impersonation');
    }
  };

  // Debug / Assume-User menu entries (NON-PRODUCTION ONLY). Availability is
  // driven by the backend /state probe (debugModeAvailable). "Assume User" is
  // offered to admins not already impersonating; while impersonating, only the
  // "Stop" action is shown (the effective user may not be an admin).
  const debugMenuItems: MenuProps['items'] = [];
  if (debugModeAvailable && isImpersonating) {
    debugMenuItems.push(
      {
        key: 'stop-impersonating',
        icon: <ExperimentOutlined />,
        label: 'Stop Assuming User',
        onClick: handleStopImpersonating,
      },
      { type: 'divider' },
    );
  } else if (debugModeAvailable && currentUser?.is_admin) {
    debugMenuItems.push(
      {
        key: 'assume-user',
        icon: <ExperimentOutlined />,
        label: 'Debug Mode — Assume User',
        onClick: () => setAssumeModalOpen(true),
      },
      { type: 'divider' },
    );
  }

  const menuItems: MenuProps['items'] = [
    {
      key: 'header',
      type: 'group',
      label: (
        <div style={{ padding: '4px 0' }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--rb-color-text-dark-primary)' }}>
            {currentUser?.full_name}
          </div>
          <div style={{ fontSize: 12, color: 'var(--rb-color-text-dark-secondary)' }}>
            {currentUser?.email}
          </div>
          {roleLabel && (
            <div style={{ fontSize: 12, color: 'var(--rb-color-text-dark-secondary)', marginTop: 2 }}>
              Role: <span style={{ color: 'var(--rb-color-text-dark-primary)', fontWeight: 500 }}>{roleLabel}</span>
            </div>
          )}
        </div>
      ),
    },
    { type: 'divider' },
    {
      key: 'help-center',
      icon: <QuestionCircleOutlined />,
      label: 'Help Center',
      onClick: () => navigate('/help', { state: { from: location.pathname } }),
    },
    ...debugMenuItems,
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign Out',
      danger: true,
      onClick: handleLogout,
    },
  ];

  return (
    <header
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 24px',
        height: 56,
        background: 'var(--rb-color-surface-dark)',
        color: 'white',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}
    >
      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div
          style={{
            background: 'linear-gradient(135deg, #1B6AEE 0%, #6366f1 100%)',
            padding: 10,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            width: 40,
            height: 40,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m8 3 4 8 5-5 5 15H2L8 3z" />
          </svg>
        </div>
        <div>
          <h1 style={{ fontFamily: 'var(--rb-font-headline)', fontSize: 26, margin: 0, lineHeight: 1, letterSpacing: -0.5 }}>
            PEAK
          </h1>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', margin: 0, marginTop: 4, fontWeight: 500 }}>
            Elevating Today's Budgets to Tomorrow's Horizon
          </p>
        </div>
      </div>

      {/* Logout overlay — shown immediately on click to prevent frozen-app feel */}
      {loggingOut && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(0, 15, 30, 0.6)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            backdropFilter: 'blur(2px)',
          }}
        >
          <Spin size="large" style={{ color: 'white' }} />
          <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: 15, fontWeight: 500 }}>
            Signing out…
          </span>
        </div>
      )}

      {/* User identity: name + role tag + avatar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {currentUser && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.2 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.95)' }}>
              {currentUser.full_name}
            </span>
            {roleLabel && (
              <Tag
                aria-label={`Role: ${roleLabel}`}
                style={{
                  marginTop: 4,
                  marginRight: 0,
                  background: 'rgba(255,255,255,0.12)',
                  color: 'rgba(255,255,255,0.85)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  fontSize: 11,
                  lineHeight: '16px',
                  padding: '0 8px',
                  borderRadius: 999,
                }}
              >
                {roleLabel}
              </Tag>
            )}
          </div>
        )}

        <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
          <div
            aria-label="User menu"
            role="button"
            tabIndex={0}
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: 'var(--rb-color-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: 16,
              fontWeight: 700,
              color: 'white',
              border: '2px solid rgba(255,255,255,0.3)',
              transition: 'border-color 0.2s',
            }}
          >
            {initials}
          </div>
        </Dropdown>
      </div>

      {/* Debug / Assume-User search modal (rendered only when available) */}
      {debugModeAvailable && (
        <AssumeUserModal open={assumeModalOpen} onClose={() => setAssumeModalOpen(false)} />
      )}
    </header>
  );
}
